export type TransactionKind =
  | "tower-purchase"
  | "tower-upgrade"
  | "tower-sale"
  | "enemy-reward"
  | "wave-reward"
  | "early-wave-reward";
export interface EconomyTransaction {
  readonly id: string;
  readonly kind: TransactionKind;
  readonly amount: number;
  readonly balanceAfter: number;
  readonly reference?: string;
}

/** The only system allowed to change player currency. */
export class EconomyManager {
  private balanceValue: number;
  private readonly processed = new Set<string>();
  readonly history: EconomyTransaction[] = [];
  constructor(
    startingCurrency: number,
    private readonly developmentLogging = false,
  ) {
    this.balanceValue = Math.max(0, Math.floor(startingCurrency));
  }
  get balance(): number {
    return this.balanceValue;
  }
  setAuthoritativeBalance(amount: number): void {
    this.balanceValue = Math.max(0, Math.floor(amount));
  }
  canAfford(amount: number): boolean {
    return amount >= 0 && this.balanceValue >= amount;
  }
  spend(
    id: string,
    kind: "tower-purchase" | "tower-upgrade",
    amount: number,
    reference?: string,
  ): boolean {
    if (this.processed.has(id) || !this.canAfford(amount)) return false;
    return this.record(id, kind, -Math.floor(amount), reference);
  }
  award(
    id: string,
    kind: Exclude<TransactionKind, "tower-purchase" | "tower-upgrade">,
    amount: number,
    reference?: string,
  ): boolean {
    if (this.processed.has(id) || amount < 0) return false;
    return this.record(id, kind, Math.floor(amount), reference);
  }
  sellRefund(totalInvestment: number, refundRate: number): number {
    return Math.max(0, Math.floor(totalInvestment * Math.max(0, Math.min(1, refundRate))));
  }
  private record(id: string, kind: TransactionKind, delta: number, reference?: string): boolean {
    this.processed.add(id);
    this.balanceValue += delta;
    const transaction: EconomyTransaction = {
      id,
      kind,
      amount: delta,
      balanceAfter: this.balanceValue,
      ...(reference === undefined ? {} : { reference }),
    };
    this.history.push(transaction);
    if (this.developmentLogging) console.debug("[economy]", transaction);
    return true;
  }
}
